import { Box, FormHelperText, TextField } from '@mui/material';
import { useFormikContext } from 'formik';
import { Duration } from 'luxon';
import { type FC, useState } from 'react';

import { hasValue } from '@core/utils/equality';
import { SetFormikValue } from '@core/utils/formikHelpers';
import {
  REMINDER_MIN_OFFSET_AMOUNT,
  REMINDER_MINUTE_OPTIONS,
  REMINDER_OFFSET_UNIT_OPTIONS,
  type ReminderOffsetUnit,
  reminderOffsetAmountForUnit,
  reminderOffsetUnit,
} from '@core/utils/reminders';

import { Selector } from '../Selector/Selector';

type Props = {
  name: string;
  disabled?: boolean;
};

export const DurationOffsetSelector: FC<Props> = ({ name, disabled }) => {
  const formikBag = useFormikContext<Record<string, unknown>>();

  const { value, error, touched } = formikBag.getFieldMeta<string>(name);
  // A picked unit only holds for the value it was picked on, so a reset or any
  // other outside write falls back to the unit the duration itself implies.
  const [selectedUnit, setSelectedUnit] = useState<{
    unit: ReminderOffsetUnit;
    forValue: string;
  } | null>(null);
  const unit = selectedUnit?.forValue === value ? selectedUnit.unit : reminderOffsetUnit(value);
  const hasError = touched && hasValue(error);

  const amount = Math.round(Duration.fromISO(value).as(unit));

  const setOffset = (nextUnit: ReminderOffsetUnit, nextAmount: number): void => {
    const nextValue = Duration.fromObject({ [nextUnit]: nextAmount }).toISO();
    setSelectedUnit({ unit: nextUnit, forValue: nextValue });
    SetFormikValue(formikBag, name, nextValue);
  };

  return (
    <Box display='flex' flexDirection='column'>
      <Box display='flex' gap={2}>
        <Box>
          {unit === 'minutes' ? (
            <Selector
              label='Amount'
              disabled={disabled}
              value={[String(amount)]}
              options={REMINDER_MINUTE_OPTIONS.map((option) => ({ id: option, name: option }))}
              sx={{ width: '100%' }}
              onChange={([selected]): void => {
                setOffset('minutes', Number(selected));
              }}
            />
          ) : (
            <TextField
              fullWidth
              sx={{ width: '15rem' }}
              id={name}
              name={name}
              type='number'
              label='Amount'
              disabled={disabled}
              error={hasError}
              value={String(amount)}
              onChange={(event): void => {
                const parsed = parseInt(event.target.value, 10);
                setOffset(unit, Number.isNaN(parsed) ? 0 : parsed);
              }}
              onBlur={(): void => {
                if (amount < REMINDER_MIN_OFFSET_AMOUNT) {
                  setOffset(unit, REMINDER_MIN_OFFSET_AMOUNT);
                }
                formikBag.setFieldTouched(name, true);
              }}
              inputProps={{ min: REMINDER_MIN_OFFSET_AMOUNT }}
            />
          )}
        </Box>
        <Box>
          <Selector
            label='Unit'
            disabled={disabled}
            value={[unit]}
            options={REMINDER_OFFSET_UNIT_OPTIONS}
            onChange={([selected]): void => {
              const nextUnit = REMINDER_OFFSET_UNIT_OPTIONS.find(
                (option) => option.id === selected,
              )?.id;
              if (nextUnit === undefined) {
                return;
              }

              setOffset(nextUnit, reminderOffsetAmountForUnit(nextUnit, amount));
            }}
          />
        </Box>
      </Box>
      <FormHelperText error={hasError}>{hasError ? error : ' '}</FormHelperText>
    </Box>
  );
};
